import { DialogContent, Divider } from '@material-ui/core';
import React from 'react';
import DialogView from '../../components/UI/DialogView';
import Column from '../../components/UI/Containers/Column';
import { HeaderText, SubtitleText } from '../../components/UI/Typography/PageHeader';
import { useTranslation } from 'react-i18next';
import Row from '../../components/UI/Containers/Row';
import CloseButton from '../BankIntegration/components/CloseButton';
import {
  Globe,
  LogOut,
  LucideIcon,
  ShieldCheck,
  User2Icon,
  Wallet,
  X,
} from 'lucide-react';
import { ArrowForwardIcon } from '../Market/components/ArrowBackIcon';
import { cn } from '../../utils';
import { OutlineFlatBtn, SecondaryFlatBtn } from '../../components/UI/PrimaryButton';
import LanguageToggle from '../../components/LanguageToggleButton';
import StatusChip from '../Murabaha/Tasks/components/StatusChip';
import { useUserRoleType } from './hooks/useUserRoleType';
import { useStateDispatch } from '../../store';
import { showConfirmDialog } from '../../components/ConfirmationDialog';
import { changeUserType, logoutUser } from '../../store/user';
import { useHistory } from 'react-router';
import AuthRoutes from '../../auth/configs/auth.route';
import ConfirmationAlertView from '../Property/PropertyView/components/ConfirmationAlertView';
import LogoutImage from '../../assets/images/logout.svg';

const AccountSettingsView = ({
  isOpen,
  setOpen,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();

  // handle logout
  const handleLogout = () => {
    const language = localStorage.getItem('language');
    localStorage.clear();
    localStorage.setItem('language', language);
    sessionStorage.clear();
    dispatch(logoutUser());
  };

  const { user, isAdmin } = useUserRoleType();
  const history = useHistory();

  //
  const [logoutAlertOpen, setLogoutAlertOpen] = React.useState(false);

  return (
    <DialogView maxWidth="sm" isOpen={isOpen} setOpen={setOpen}>
      <DialogContent className="col gap-5 w-full !p-7">
        {/* Account settings texts */}
        <Row className="w-full justify-between">
          <Column>
            <HeaderText text={t('account.settings')} />
            <SubtitleText text={t('account.prefernces')} />
          </Column>

          {/* Close Button */}
          <CloseButton onClick={() => setOpen(false)} />
        </Row>
        <Column className="p-5 border border-border2Color rounded-xl gap-5">
          {/* User Avatar */}
          <Row className="gap-5">
            {/* Avatar */}
            <div className="w-16 h-16 bg-secondary rounded-full centerDiv text-sm font-semibold text-white">
              {`#${user.id}`}
            </div>

            {/* Name Card */}
            <Column className="gap-0">
              <HeaderText className="text-base" text={user.fullName} />
              <SubtitleText
                dir="ltr"
                className="text-[13px] self-start"
                text={user.mobile_number}
              />
              <SubtitleText className="text-[13px]" text={user.email} />
            </Column>
          </Row>

          {/* Divider */}
          <Divider />

          {/* Profile */}
          <SettingsCard
            icon={User2Icon}
            title={t('components.header.profile')}
            subtitle={t('account.view_personal_info')}
            onClick={() => {
              setOpen(false);
              history.push(AuthRoutes.PROFILE);
            }}
          />

          {/* Accunts */}
          <SettingsCard
            icon={Wallet}
            className="bg-purpleBg text-purpleText"
            title={t('components.header.accounts')}
            subtitle={t('account.manage_account')}
            onClick={() => {
              setOpen(false);
              history.push(
                isAdmin ? AuthRoutes.IBDAX_ACCOUNTS : AuthRoutes.INVESTOR_ACCOUNTS
              );
            }}
          />

          {/* Role */}
          <SettingsCard
            icon={ShieldCheck}
            className="bg-unitsBg text-primaryGreen"
            title={t('account.role')}
            subtitle={t('account.role_subtitle')}
          >
            <UserRoleChips onChangeRole={() => setOpen(false)} />
          </SettingsCard>

          {/* Langugages */}
          <SettingsCard
            icon={Globe}
            className="bg-warningBg text-yellowColor"
            title={t('account.language')}
            subtitle={t('account.language_sub')}
            isHasBorder={false}
          >
            <LanguageToggle />
          </SettingsCard>
        </Column>

        {/* Divider */}
        <Divider />

        {/* Logout and close */}
        <Row className="w-full gap-5">
          <OutlineFlatBtn
            isFullWidth
            leadingIcon={<LogOut size={16} className="text-red-500" />}
            onClick={() => setLogoutAlertOpen(true)}
            title={t('components.header.logout')}
            className="border-red-500 text-red-500 hover:bg-red-50"
          />
          <SecondaryFlatBtn
            isFullWidth
            title={t('pages.accounts.admin.withdrawals.close')}
            onClick={() => setOpen(false)}
          />
        </Row>
      </DialogContent>

      <ConfirmationAlertView
        title={t('auth.logout')}
        subTitle={t('auth.logout_description')}
        //
        imageIcon={LogoutImage}
        confirmBtnText={t('common.yes')}
        cancelBtnText={t('common.no')}
        isOpen={logoutAlertOpen}
        setOpen={setLogoutAlertOpen}
        onConfirm={handleLogout}
      />
    </DialogView>
  );
};

export default AccountSettingsView;

const SettingsCard = ({
  title,
  subtitle,
  onClick,
  className,
  children,
  icon: Icon,
  //
  isHasBorder = true,
}: {
  title: string;
  subtitle: string;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  icon: LucideIcon;
  //
  isHasBorder?: boolean;
}) => {
  return (
    <Row
      onClick={onClick}
      className={cn(
        'gap-2 items-center pb-4 cursor-pointer animated', // hover:scale-105

        isHasBorder ? 'border-b border-border2Color' : 'border-none'
      )}
    >
      <Row className="w-full gap-4">
        <div
          className={cn(
            'bg-secondaryBg text-secondary w-10 h-10 centerDiv rounded-lg',
            className
          )}
        >
          <Icon size={18} />
        </div>

        <Column className="gap-1 max-w-full">
          <HeaderText text={title} className="text-sm" />
          <SubtitleText className="text-xs" text={subtitle} />
        </Column>
      </Row>

      {children ?? <ArrowForwardIcon className="mt-1" />}
    </Row>
  );
};

const UserRoleChips = ({
  onChangeRole,
}: {
  onChangeRole?: (role: string) => void;
}) => {
  const { roles, currentRole } = useUserRoleType();
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const history = useHistory();

  const handleChangeUserType = async (value: string) => {
    if (!value || value === currentRole) return;
    try {
      await showConfirmDialog({
        title: t('components.header.redirect_message'),
      } as any);
      history.push('/');
      dispatch(changeUserType(value) as any);
      onChangeRole?.(value);
    } catch (err) {}
  };

  return (
    <Row className="gap-2">
      {roles.map((role) => (
        <StatusChip
          key={role}
          onClick={() => handleChangeUserType(role)}
          className="text-xs"
          status={role === currentRole ? 'COMPLETED' : 'PENDING'}
          isShowDot={false}
          label={t(`components.header.${role}`).split(' ')[0]}
        />
      ))}
    </Row>
  );
};
